import React, { forwardRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { InlineLoading } from '../LoadingSpinner/LoadingSpinner';
import { UnstyledButton } from './Button';

// Animations for inline save button
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const InputContainer = styled.div`
  width: 100%;
  margin-bottom: ${props => props.$marginBottom || '16px'};
  position: ${props => props.$variant === 'floating' ? 'relative' : 'static'};
  
  @media (max-width: 768px) {
    margin-bottom: ${props => {
      const mb = props.$marginBottom;
      if (mb === '0' || mb === '0px') return '0';
      return '10px';
    }};
  }
`;

// Wrapper for input field + save button to enable proper vertical centering
const InputFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

// Inline save button for inputs
const InlineSaveButton = styled(UnstyledButton)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
  animation: ${fadeIn} 0.2s ease;
  
  ${props => props.$status === 'dirty' && css`
    background: ${props.theme.colors.primary};
    color: white;
    border: none;
    
    &:hover {
      background: ${props.theme.colors.primaryDark || props.theme.colors.primary};
      transform: translateY(-50%) scale(1.02);
    }
    
    &:active {
      transform: translateY(-50%) scale(0.98);
    }
  `}
  
  ${props => props.$status === 'saving' && css`
    background: ${props.theme.colors.primary};
    color: white;
    border: none;
    pointer-events: none;
  `}
  
  ${props => props.$status === 'saved' && css`
    background: #10b981;
    color: white;
    border: none;
    pointer-events: none;
  `}
  
  ${props => props.$status === 'error' && css`
    background: #ef4444;
    color: white;
    border: none;
  `}
`;

const InputLabel = styled.label`
  display: ${props => props.$variant === 'floating' ? 'block' : 'block'};
  margin-bottom: ${props => props.$variant === 'floating' ? '0' : '6px'};
  font-weight: ${props => props.$variant === 'floating' ? '400' : '500'};
  color: ${props => {
    // Custom label color takes priority
    if (props.$labelColor) {
      return props.$labelColor;
    }
    if (props.$variant === 'floating') {
      return props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary;
    }
    return props.theme.colors.text;
  }};
  font-size: ${props => {
    // Custom label font size takes priority
    if (props.$labelFontSize) {
      return props.$active ? '12px' : props.$labelFontSize;
    }
    return props.$variant === 'floating' && props.$active ? '12px' : props.$variant === 'floating' ? '16px' : '14px';
  }};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    font-size: ${props => {
      if (props.$labelFontSize) {
        return props.$active ? '10px' : '13px';
      }
      return props.$variant === 'floating' && props.$active ? '10px' : props.$variant === 'floating' ? '14px' : '13px';
    }};
  }
  
  /* Floating label positioning */
  ${props => props.$variant === 'floating' && `
    position: absolute;
    left: 16px;
    top: ${props.$active ? '0' : '50%'};
    transform: translateY(-50%);
    background: ${props.theme.colors.bg || 'white'};
    padding: 0 4px;
    z-index: 2;
    pointer-events: none;
    
    @media (max-width: 768px) {
      left: 12px;
    }
  `}
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${props.theme.colors.error};
    }
  `}
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.$variant === 'floating' ? '12px 16px' : '12px 16px'};
  border: 2px solid ${props => 
    props.$error ? props.theme.colors.error : 
    props.$focused ? props.theme.colors.primary :
    props.theme.colors.border
  };
  border-radius: ${props => props.$rounded || props.$variant === 'floating' ? '50px' : '8px'};
  font-size: 16px;
  font-family: inherit;
  background: ${props => props.theme.colors.bg || 'white'};
  color: ${props => props.theme.colors.text};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: ${props => props.$variant === 'floating' ? '10px 12px' : '10px 12px'};
    font-size: 14px;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 ${props => props.$variant === 'floating' ? '2px' : '3px'} ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.$variant === 'floating' ? 'transparent' : props.theme.colors.textSecondary};
    transition: color 0.2s ease;
  }
  
  &:focus::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  &:disabled {
    background: ${props => props.theme.colors.bg2};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  /* Hide native date picker indicator when custom icon is used */
  ${props => props.$hideNativePicker && `
    &::-webkit-calendar-picker-indicator {
      opacity: 0;
      position: absolute;
      right: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  `}
  
  /* Ensure minimum 16px font size on mobile to prevent zoom */
  @media (max-width: 768px) {
    font-size: 16px;
    min-height: ${props => props.$variant === 'floating' ? '40px' : 'auto'};
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.$variant === 'floating' ? '16px' : '12px 16px'};
  border: 2px solid ${props => 
    props.$error ? props.theme.colors.error : 
    props.$focused ? props.theme.colors.primary :
    props.theme.colors.border
  };
  border-radius: ${props => props.$variant === 'floating' ? '30px' : '8px'};
  font-size: 16px;
  font-family: inherit;
  background: ${props => props.theme.colors.bg || 'white'};
  color: ${props => props.theme.colors.text};
  resize: vertical;
  min-height: ${props => props.$minHeight || (props.$variant === 'floating' ? '120px' : '100px')};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 ${props => props.$variant === 'floating' ? '2px' : '3px'} ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.$variant === 'floating' ? 'transparent' : props.theme.colors.textSecondary};
    transition: color 0.2s ease;
  }
  
  &:focus::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  &:disabled {
    background: ${props => props.theme.colors.bg2};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ErrorMessage = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  margin-top: 4px;
  margin-left: 16px;
  display: block;
  opacity: 0.9;
  letter-spacing: 0.01em;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 0.9; transform: translateY(0); }
  }
`;

const HelperText = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  margin-top: 4px;
  display: block;
`;

// Info message - subtle hint text below input
const InfoMessage = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  margin-top: 4px;
  margin-left: 16px;
  display: block;
  opacity: 0.85;
  letter-spacing: 0.01em;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 0.85; transform: translateY(0); }
  }
`;

const InputWithIcon = styled.div`
  position: relative;
  
  .input-icon {
    position: absolute;
    ${props => props.$iconPosition === 'right' ? 'right: 12px;' : 'left: 12px;'}
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.colors.textSecondary};
    pointer-events: none;
    z-index: 2;
  }
  
  ${StyledInput} {
    padding-left: ${props => props.hasIcon && props.$iconPosition !== 'right' ? '44px' : '16px'};
    padding-right: ${props => props.hasIcon && props.$iconPosition === 'right' ? '44px' : '16px'};
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: ${props => props.$marginBottom || '0'};
  
  &:hover {
    .checkbox-box {
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const CheckboxBox = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => 
    props.checked ? props.theme.colors.primary : props.theme.colors.border
  };
  border-radius: 4px;
  background: ${props => 
    props.checked ? props.theme.colors.primary : 'transparent'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-right: 8px;
  flex-shrink: 0;
  
  ${props => props.checked && `
    &:after {
      content: '✓';
      color: white;
      font-size: 12px;
      font-weight: bold;
    }
  `}
`;

const CheckboxLabel = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  line-height: 1.4;
`;

export const Input = forwardRef(({ 
  label, 
  error, 
  helperText,
  info, // Info message to display below input
  infoType, // 'success' | 'warning' | 'error' | 'info' (default)
  required,
  marginBottom,
  icon,
  variant = 'default', // 'default' or 'floating'
  labelColor, // Custom label color (e.g., '#1a1a2e' for dark labels)
  labelFontSize, // Custom label font size (e.g., '14px')
  rounded, // Make input pill-shaped (border-radius: 50px)
  // Inline save props
  saveStatus, // 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  onSave, // Function to call when save button is clicked
  saveLabel = 'Save', // Label for save button
  ...props 
}, ref) => {
  const [focused, setFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(!!props.value || !!props.defaultValue);
  
  // For floating labels, determine if label should be in active (floating) state
  const isLabelActive = variant === 'floating' && (focused || hasValue || props.value);
  
  // Show save button when status is not idle
  const showSaveButton = saveStatus && saveStatus !== 'idle' && onSave;
  
  // For date/time inputs with icon, position icon on right and hide native picker
  const isDateInput = ['date', 'time', 'datetime-local'].includes(props.type);
  const iconPosition = isDateInput && icon ? 'right' : 'left';

  const handleFocus = (e) => {
    setFocused(true);
    props.onFocus?.(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    setHasValue(!!e.target.value);
    props.onBlur?.(e);
  };
  
  const handleChange = (e) => {
    setHasValue(!!e.target.value);
    props.onChange?.(e);
  };

  const inputElement = (
    <StyledInput 
      ref={ref}
      $error={error} 
      $focused={focused}
      $variant={variant}
      $rounded={rounded}
      $hideNativePicker={isDateInput && icon}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      style={{ paddingRight: showSaveButton ? '70px' : undefined, ...props.style }}
      {...props} 
    />
  );
  
  const saveButton = showSaveButton && (
    <InlineSaveButton
      type="button"
      onClick={onSave}
      disabled={saveStatus === 'saving'}
      $status={saveStatus}
      title={saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Error - Click to retry' : 'Save changes'}
    >
      {saveStatus === 'saving' ? (
        <InlineLoading size={14} />
      ) : saveStatus === 'saved' ? (
        <VipIcon name="Checkmark" size={14} />
      ) : (
        saveLabel
      )}
    </InlineSaveButton>
  );

  return (
    <InputContainer $marginBottom={marginBottom} $variant={variant}>
      {variant === 'floating' ? (
        <InputFieldWrapper>
          {inputElement}
          {saveButton}
          {label && (
            <InputLabel 
              required={required} 
              $variant={variant}
              $active={isLabelActive}
              $labelColor={labelColor}
              $labelFontSize={labelFontSize}
            >
              {label}
            </InputLabel>
          )}
        </InputFieldWrapper>
      ) : (
        <>
          {label && (
            <InputLabel required={required} $variant={variant}>
              {label}
            </InputLabel>
          )}
          <InputFieldWrapper>
            {icon ? (
              <InputWithIcon hasIcon={!!icon} $iconPosition={iconPosition}>
                <div className="input-icon">{icon}</div>
                {inputElement}
              </InputWithIcon>
            ) : (
              inputElement
            )}
            {saveButton}
          </InputFieldWrapper>
        </>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {info && !error && <InfoMessage $type={infoType}>{info}</InfoMessage>}
    </InputContainer>
  );
});

export const TextArea = forwardRef(({ 
  label, 
  error, 
  helperText,
  info, // Info message to display below textarea
  infoType, // 'success' | 'warning' | 'error' | 'info' (default)
  required,
  marginBottom,
  minHeight,
  variant = 'default', // 'default' or 'floating'
  labelColor,
  labelFontSize,
  // Inline save props
  saveStatus, // 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  onSave, // Function to call when save button is clicked
  saveLabel = 'Save', // Label for save button
  ...props 
}, ref) => {
  const [focused, setFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(!!props.value || !!props.defaultValue);
  
  // For floating labels, determine if label should be in active (floating) state
  const isLabelActive = variant === 'floating' && (focused || hasValue || props.value);
  
  // Show save button when status is not idle
  const showSaveButton = saveStatus && saveStatus !== 'idle' && onSave;

  const handleFocus = (e) => {
    setFocused(true);
    props.onFocus?.(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    setHasValue(!!e.target.value);
    props.onBlur?.(e);
  };
  
  const handleChange = (e) => {
    setHasValue(!!e.target.value);
    props.onChange?.(e);
  };

  const textAreaElement = (
    <StyledTextArea 
      ref={ref}
      $error={error} 
      $focused={focused}
      $minHeight={minHeight}
      $variant={variant}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      style={{ paddingRight: showSaveButton ? '70px' : undefined, ...props.style }}
      {...props} 
    />
  );
  
  const saveButton = showSaveButton && (
    <InlineSaveButton
      type="button"
      onClick={onSave}
      disabled={saveStatus === 'saving'}
      $status={saveStatus}
      $isTextArea
      title={saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Error - Click to retry' : 'Save changes'}
    >
      {saveStatus === 'saving' ? (
        <InlineLoading size={14} />
      ) : saveStatus === 'saved' ? (
        <VipIcon name="Checkmark" size={14} />
      ) : (
        saveLabel
      )}
    </InlineSaveButton>
  );

  return (
    <InputContainer $marginBottom={marginBottom} $variant={variant}>
      {variant === 'floating' ? (
        <InputFieldWrapper>
          {textAreaElement}
          {saveButton}
          {label && (
            <InputLabel 
              required={required} 
              $variant={variant}
              $active={isLabelActive}
              $labelColor={labelColor}
              $labelFontSize={labelFontSize}
            >
              {label}
            </InputLabel>
          )}
        </InputFieldWrapper>
      ) : (
        <>
          {label && (
            <InputLabel required={required}>
              {label}
            </InputLabel>
          )}
          <InputFieldWrapper>
            {textAreaElement}
            {saveButton}
          </InputFieldWrapper>
        </>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
      {info && !error && <InfoMessage $type={infoType}>{info}</InfoMessage>}
    </InputContainer>
  );
});

export const Checkbox = forwardRef(({ 
  label, 
  checked, 
  onChange, 
  marginBottom,
  disabled,
  ...props 
}, ref) => {
  return (
    <CheckboxContainer $marginBottom={marginBottom}>
      <CheckboxInput
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
      <CheckboxBox 
        className="checkbox-box"
        checked={checked}
      />
      {label && <CheckboxLabel>{label}</CheckboxLabel>}
    </CheckboxContainer>
  );
});

// Select Icon Wrapper
const SelectIconWrapper = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.textSecondary};
`;

// Styled Select for floating variant
const StyledSelect = styled.select`
  width: 100%;
  padding: ${props => props.$variant === 'floating' ? '12px 16px' : '12px 16px'};
  padding-right: 40px;
  border: 2px solid ${props => 
    props.$error ? props.theme.colors.error : 
    props.$focused ? props.theme.colors.primary :
    props.theme.colors.border
  };
  border-radius: ${props => props.$variant === 'floating' ? '50px' : '8px'};
  font-size: 16px;
  font-family: inherit;
  background: ${props => props.theme.colors.bg || 'white'};
  color: ${props => (props.$variant === 'floating' && !props.$hasValue) ? 'transparent' : props.theme.colors.text};
  transition: border-color 0.3s ease, box-shadow 0.3s ease, color 0.2s ease;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  position: relative;
  z-index: 1;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 ${props => props.$variant === 'floating' ? '2px' : '3px'} ${props => props.theme.colors.primary}20;
  }
  
  &:disabled {
    background: ${props => props.theme.colors.bg2};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  option {
    background: ${props => props.theme.colors.bg || 'white'};
    color: ${props => props.theme.colors.text};
    padding: 8px;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

export const Select = forwardRef(({ 
  label, 
  error, 
  helperText, 
  required,
  marginBottom,
  variant = 'default', // 'default' or 'floating'
  children,
  ...props 
}, ref) => {
  const [focused, setFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(!!props.value || !!props.defaultValue);
  
  // For floating labels, determine if label should be in active (floating) state
  const isLabelActive = variant === 'floating' && (focused || hasValue || props.value);

  const handleFocus = (e) => {
    setFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setHasValue(!!e.target.value);
    props.onBlur?.(e);
  };

  const handleChange = (e) => {
    setHasValue(!!e.target.value);
    props.onChange?.(e);
  };

  return (
    <InputContainer $marginBottom={marginBottom} $variant={variant}>
      {variant === 'floating' ? (
        <InputFieldWrapper>
          <StyledSelect
            ref={ref}
            $variant={variant}
            $error={!!error}
            $focused={focused}
            $hasValue={!!props.value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          >
            {children}
          </StyledSelect>
          <SelectIconWrapper>
            <VipIcon name="ChevronDown" size={18} />
          </SelectIconWrapper>
          {label && (
            <InputLabel 
              $variant={variant}
              $active={isLabelActive}
              required={required}
            >
              {label}
            </InputLabel>
          )}
        </InputFieldWrapper>
      ) : (
        <>
          {label && (
            <InputLabel 
              $variant={variant}
              $active={isLabelActive}
              required={required}
            >
              {label}
            </InputLabel>
          )}
          <div style={{ position: 'relative' }}>
            <StyledSelect
              ref={ref}
              $variant={variant}
              $error={!!error}
              $focused={focused}
              $hasValue={!!props.value}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              {...props}
            >
              {children}
            </StyledSelect>
            <SelectIconWrapper>
              <VipIcon name="ChevronDown" size={18} />
            </SelectIconWrapper>
          </div>
        </>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
    </InputContainer>
  );
});

// Slider Component
const SliderContainer = styled.div`
  width: 100%;
  margin-bottom: ${props => props.$marginBottom || '16px'};
  position: relative;
`;

const SliderLabel = styled.label`
  display: block;
  font-weight: 400;
  color: ${props => {
    if (props.$variant === 'floating') {
      return props.theme.colors.primary;
    }
    return props.theme.colors.text;
  }};
  font-size: 12px;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'floating' && `
    position: absolute;
    left: 16px;
    top: 0;
    transform: translateY(-50%);
    background: ${props.theme.colors.bg || 'white'};
    padding: 0 4px;
    z-index: 2;
    pointer-events: none;
  `}
  
  ${props => props.$variant !== 'floating' && `
    margin-bottom: 6px;
  `}
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${props.theme.colors.error};
    }
  `}
`;

const SliderWrapper = styled.div`
  position: relative;
  padding: ${props => props.$variant === 'floating' ? '12px 16px' : '8px 0'};
  background: ${props => props.$variant === 'floating' ? props.theme.colors.bg || 'white' : 'transparent'};
  border: ${props => props.$variant === 'floating' ? `2px solid ${props.theme.colors.border}` : 'none'};
  border-radius: ${props => props.$variant === 'floating' ? '50px' : '0'};
  transition: all 0.2s ease;
  height: ${props => props.$variant === 'floating' ? '50px' : 'auto'};
  display: flex;
  align-items: center;
  
  ${props => props.$variant === 'floating' && `
    &:hover {
      border-color: ${props.theme.colors.primary};
    }
    
    &:focus-within {
      border-color: ${props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props.theme.colors.primary}15;
    }
  `}
`;

const StyledSlider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 50px;
  -webkit-appearance: none;
  background: linear-gradient(90deg, 
    ${({ theme }) => theme.colors.border} 0%, 
    ${({ theme }) => theme.colors.primaryBlue} 100%);
  outline: none;
  cursor: pointer;
  
  &::-webkit-slider-track {
    height: 8px;
    border-radius: 50px;
    background: transparent;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FC5C7D 0%, #6A82FB 100%);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.15);
    }
  }

  &::-moz-range-track {
    height: 8px;
    border-radius: 50px;
    background: linear-gradient(90deg, 
      ${({ theme }) => theme.colors.border} 0%, 
      ${({ theme }) => theme.colors.primaryBlue} 100%);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FC5C7D 0%, #6A82FB 100%);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.15);
    }
  }
`;

const SliderValue = styled.span`
  position: absolute;
  right: 16px;
  top: ${props => props.$variant === 'floating' ? '0' : '50%'};
  transform: ${props => props.$variant === 'floating' ? 'translateY(-50%)' : 'translateY(-50%)'};
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  pointer-events: none;
  background: ${props => props.$variant === 'floating' ? props.theme.colors.bg || 'white' : 'transparent'};
  padding: ${props => props.$variant === 'floating' ? '0 4px' : '0'};
  z-index: 2;
`;

export const Slider = forwardRef(({ 
  label, 
  value, 
  min = 0, 
  max = 100, 
  step = 1,
  showValue = true,
  valueFormat = (val) => `${val}`,
  variant = 'default',
  error, 
  helperText, 
  required,
  marginBottom,
  ...props 
}, ref) => {
  const formattedValue = valueFormat(value);

  return (
    <SliderContainer $marginBottom={marginBottom}>
      {label && (
        <SliderLabel 
          $variant={variant}
          required={required}
        >
          {label}
        </SliderLabel>
      )}
      <SliderWrapper $variant={variant}>
        <StyledSlider
          ref={ref}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          {...props}
        />
        {showValue && variant === 'floating' && (
          <SliderValue $variant={variant}>{formattedValue}</SliderValue>
        )}
      </SliderWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {helperText && !error && <HelperText>{helperText}</HelperText>}
    </SliderContainer>
  );
});

// SegmentedControl Component - Toggle button group with sliding indicator
const SegmentedContainer = styled.div`
  display: inline-flex;
  position: relative;
  border-radius: 50px;
  overflow: hidden;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.bg2};
  padding: 3px;
`;

const SegmentSlider = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: ${props => props.$offset}px;
  width: ${props => props.$width}px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50px;
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`;

const Segment = styled(UnstyledButton)`
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
  position: relative;
  z-index: 1;
  
  &:hover:not(:disabled):not([data-active="true"]) {
    color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

export const SegmentedControl = ({ value, onChange, options, disabled, name }) => {
  const containerRef = React.useRef(null);
  const [sliderStyle, setSliderStyle] = React.useState({ offset: 0, width: 0 });

  React.useEffect(() => {
    if (containerRef.current) {
      const activeIndex = options.findIndex(opt => opt.value === value);
      const buttons = containerRef.current.querySelectorAll('button');
      if (buttons[activeIndex]) {
        const button = buttons[activeIndex];
        setSliderStyle({
          offset: button.offsetLeft,
          width: button.offsetWidth
        });
      }
    }
  }, [value, options]);

  const handleClick = (optionValue) => {
    if (!disabled && optionValue !== value) {
      onChange({ target: { name, value: optionValue, type: 'segmented' } });
    }
  };

  return (
    <SegmentedContainer ref={containerRef}>
      <SegmentSlider $offset={sliderStyle.offset} $width={sliderStyle.width} />
      {options.map((option) => (
        <Segment
          key={option.value}
          type="button"
          $active={value === option.value}
          data-active={value === option.value}
          onClick={() => handleClick(option.value)}
          disabled={disabled}
        >
          {option.label}
        </Segment>
      ))}
    </SegmentedContainer>
  );
};

Input.displayName = 'Input';
TextArea.displayName = 'TextArea';
Checkbox.displayName = 'Checkbox';
Select.displayName = 'Select';
Slider.displayName = 'Slider';
SegmentedControl.displayName = 'SegmentedControl';
